import { AgsJackpotDisplayComponent } from "ags/components/ags-jackpot-display-component";
import { CenterPivot } from "appworks/graphics/pixi/group";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { deepClone } from "appworks/utils/collection-utils";
import { CancelGroup } from "appworks/utils/contracts/cancel-group";
import { Easing, Tween } from "appworks/utils/tween";
import { gameLayers } from "game-layers";
import { gameiomModel, JackpotTierKeys, JackpotTierNames, JackpotTiers } from "gameiom/model/gameiom-model";
import { SlotBetService } from "slotworks/services/bet/slot-bet-service";

export class ThemeJackpotDisplayComponent extends AgsJackpotDisplayComponent {
    protected boostLevels: Map<JackpotTiers, number> = new Map();
    protected currentValues: Map<JackpotTiers, number> = new Map();

    protected scaleTweenCancelGroup = new CancelGroup();

    public init() {
        this.resetBoostLevels();
        super.init();

        gameLayers.Jackpots.onSceneEnter.add(() => this.setupBoostLabels());
        gameLayers.Jackpots.onSceneEnter.add(() => this.updateValues());
    }

    public setBoostLevel(tier: JackpotTiers, level: number) {
        const currentLevel = this.boostLevels.get(tier);
        if (level === currentLevel) { return; }

        const boostText = gameLayers.Jackpots.getText(`${JackpotTierNames.get(tier)}_boost`);
        const hitAnim = gameLayers.Jackpots.getAnimatedSprite(`${JackpotTierNames.get(tier)}_boosted_hit`);
        const level1Anim = gameLayers.Jackpots.getAnimatedSprite(`${JackpotTierNames.get(tier)}_level_1_boosted`);
        const level2Anim = gameLayers.Jackpots.getAnimatedSprite(`${JackpotTierNames.get(tier)}_level_2_boosted`);

        if (level > currentLevel) {
            hitAnim.visible = true;
            hitAnim.gotoAndPlayOnce(0).then(() => hitAnim.visible = false);

            boostText.visible = true;
        }

        switch (level) {
            case 1:
                level1Anim.visible = true;
                level2Anim.visible = false;

                level1Anim.play();
                level2Anim.stop();
                break;

            case 2:
            case 3:
                level1Anim.visible = false;
                level2Anim.visible = true;

                level1Anim.stop();
                level2Anim.play();
                break;

            default:
                level1Anim.visible = false;
                level2Anim.visible = false;

                level1Anim.stop();
                level2Anim.stop();

                boostText.visible = false;
        }

        this.boostLevels.set(tier, level);
    }

    public updateValues(animateValues: boolean = false): void {
        this.updateTextInstances();

        const lastValues = deepClone(this.currentValues);
        const betMultiplier = Services.get(SlotBetService).getCreditPerLine() / Services.get(SlotBetService).getMinimumCredit();
     
        JackpotTierKeys.forEach((tier) => {
            const lastValue: number = lastValues.get(tier);
            const currentValue: number =  gameiomModel.jackpotAmounts.get(tier) * betMultiplier;
            this.currentValues.set(tier, currentValue);

            // Don't animate if the value hasn't changed to stop text showing as "NaN"
            const animateValue: boolean = animateValues && currentValue !== lastValue;

            if (animateValue) {
                const tweenObj = { value: lastValue || 0 };
                new Tween(tweenObj)
                    .to({ value: currentValue }, 500)
                    .easing(Easing.Cubic.Out)
                    .onUpdate(() => {
                        const roundedValue = Math.round(tweenObj.value / 100) * 100; // Round to nearest hundred so we don't show pennies during tween
                        this.textInstances.get(tier).text = Services.get(CurrencyService).format(roundedValue, false, false);
                    })
                    .start();
            } else {
                this.textInstances.get(tier).text = Services.get(CurrencyService).format(currentValue, false, false);
            }
        });
    }

    public resetBoostLevels() {
        this.boostLevels.set(JackpotTiers.MAJOR, 0);
        this.boostLevels.set(JackpotTiers.GRAND, 0);
    }

    protected setupBoostLabels() {
        this.scaleTweenCancelGroup.cancel();

        const labels = ["grand_boost", "major_boost", "minor_boost", "mini_boost"].map((val) => gameLayers.Jackpots.getText(val));
        for (const lbl of labels) {
            if (!lbl) { continue; }

            CenterPivot(lbl).angle = -20;
            lbl.text = `+${lbl.text}`;

            for (const orientation of ["landscape", "portrait"]) {
                this.scaleTweenCancelGroup.tween(lbl[orientation].scale)
                    .to({ x: lbl.landscape.scale.x * 1.2, y: lbl.landscape.scale.y * 1.2 }, 500)
                    .easing(Easing.Sinusoidal.InOut)
                    .yoyo(true)
                    .repeat(Infinity)
                    .start();
            }

            gameLayers.Jackpots.add(lbl); // boost text is underneath the value for some reason
            lbl.visible = false;
        }
    }
}
